import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import useVendors from "../../../hooks/useVendors";
import useCreatePurchaseOrders from "../../../hooks/useCreatePurchaseOrders";
import useProcurementCart, { isValidCartQuantity } from "./cartStore";
import BatchStockPanel from "./BatchStockPanel";
import { VendorSelect, stripsReadout } from "../shared";

// The cart (2026-10-30): medicines added from any page/tab of the shortage screen ("Add to
// cart" on a row adds straight away), then reviewed here on ONE screen — every quantity is set
// here — and raised as ONE purchase order to ONE vendor: POST /api/inventory/orders with a
// single group of many items. A quantity starts as the listing's suggestion (eVital's bulk
// total, near-expiry batches included); each line's info button opens that medicine's
// batch-wise stock and the suggestion recomputed from it (BatchStockPanel).

function StepHeading({ step, title, aside }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-bold text-[var(--ops-carbon-900)]">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--ops-carbon-900)] text-[11px] font-bold text-[var(--ops-carbon-25)]">
                    {step}
                </span>
                {title}
            </h3>
            {aside}
        </div>
    );
}
StepHeading.propTypes = { step: PropTypes.number.isRequired, title: PropTypes.node.isRequired, aside: PropTypes.node };

function CartModal({ onClose }) {
    const cart = useProcurementCart();
    const { data: vendors = [] } = useVendors();
    const createPurchaseOrders = useCreatePurchaseOrders();
    const [vendorId, setVendorId] = useState("");
    const [infoItemId, setInfoItemId] = useState(null); // line whose batch-wise stock is open

    const lineCount = cart.lines.length;
    const invalidCount = cart.lines.filter((line) => !isValidCartQuantity(line.quantity)).length;
    const totalTablets = cart.lines.reduce((sum, line) => sum + (isValidCartQuantity(line.quantity) ? parseInt(line.quantity, 10) : 0), 0);
    const allValid = lineCount > 0 && invalidCount === 0;
    const canPlace = allValid && !!vendorId && !createPurchaseOrders.isLoading;
    // Why the primary button is disabled, said out loud rather than left to guess.
    let blocker = null;
    if (!vendorId) blocker = "Choose a vendor to continue";
    else if (invalidCount > 0) blocker = `Enter a quantity for ${invalidCount} medicine${invalidCount === 1 ? "" : "s"}`;

    const handlePlace = async () => {
        if (!canPlace) return;
        try {
            const created = await createPurchaseOrders.mutateAsync([
                {
                    vendorId: parseInt(vendorId, 10),
                    items: cart.lines.map((line) => ({ itemId: line.itemId, quantityOrdered: parseInt(line.quantity, 10) })),
                },
            ]);
            const count = cart.lines.length;
            toast.success(`Created purchase order${created?.[0]?.id ? ` #${created[0].id}` : ""} for ${count} medicine${count === 1 ? "" : "s"}`);
            cart.clear();
            onClose();
        } catch (error) {
            toast.error(error?.err || "Error creating purchase order");
        }
    };

    return (
        <Modal
            title={(
                <span>
                    Purchase order
                    <span className="ml-2 text-sm font-semibold text-[var(--ops-carbon-500)]">
                        {lineCount} medicine{lineCount === 1 ? "" : "s"}
                    </span>
                </span>
            )}
            onClose={onClose}
            size="max-w-lg sm:max-w-3xl md:max-w-4xl"
            footer={(
                <>
                    <div className="mr-auto text-sm">
                        <div className="font-semibold text-[var(--ops-carbon-900)]">
                            {`${lineCount} medicine${lineCount === 1 ? "" : "s"} · ${totalTablets.toLocaleString()} tablets`}
                        </div>
                        {blocker && lineCount > 0 && <div className="text-xs text-[var(--ops-amber-700)]">{blocker}</div>}
                    </div>
                    <button type="button" className="px-4 py-2 rounded-lg border border-[var(--ops-carbon-200)] text-sm font-semibold" onClick={onClose}>
                        Keep shopping
                    </button>
                    <button
                        type="button"
                        onClick={handlePlace}
                        disabled={!canPlace}
                        className="bg-elm-500 text-white-50 px-4 py-2 rounded-lg font-sans font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {createPurchaseOrders.isLoading ? "Creating…" : "Create Purchase Order"}
                    </button>
                </>
            )}
        >
            {lineCount === 0 ? (
                <p className="text-sm text-[var(--ops-carbon-500)]">Your cart is empty. Use &ldquo;Add to cart&rdquo; on a medicine to start.</p>
            ) : (
                <div className="space-y-6">
                    <section aria-label="Vendor">
                        <StepHeading step={1} title="Choose a vendor" />
                        <div className="mt-2 max-w-[380px]">
                            <VendorSelect row={{}} value={vendorId} onChange={(_itemId, value) => setVendorId(value)} vendors={vendors} />
                        </div>
                    </section>

                    <section aria-label="Medicines">
                        <StepHeading
                            step={2}
                            title="Set quantities"
                            aside={(
                                <button type="button" onClick={cart.clear} className="text-xs font-semibold text-[var(--ops-carbon-500)] hover:text-[var(--ops-crimson-600)] hover:underline">
                                    Clear cart
                                </button>
                            )}
                        />
                        <ul className="mt-2 space-y-3">
                            {cart.lines.map((line) => {
                                const valid = isValidCartQuantity(line.quantity);
                                const readout = valid ? stripsReadout(parseInt(line.quantity, 10), line.stripSize) : null;
                                const infoOpen = infoItemId === line.itemId;
                                return (
                                    <li key={line.itemId} className="overflow-hidden rounded-xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
                                            <div className="min-w-[180px] flex-1">
                                                <div className="text-[15px] font-bold text-[var(--ops-carbon-900)]">{line.medicineName}</div>
                                                <div className="text-xs text-[var(--ops-carbon-500)]">
                                                    {line.stripSize != null ? `${line.stripSize} tablets / strip` : "Strip size unknown"}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--ops-carbon-500)]" htmlFor={`cart-qty-${line.itemId}`}>
                                                    Quantity (tablets)
                                                </label>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <input
                                                        id={`cart-qty-${line.itemId}`}
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        value={line.quantity}
                                                        onChange={(event) => cart.setQuantity(line.itemId, event.target.value)}
                                                        className={`w-28 h-9 border rounded-lg px-2.5 text-sm font-semibold ${valid ? "border-[var(--ops-carbon-200)]" : "border-[var(--ops-crimson-400)]"}`}
                                                        aria-label={`Quantity for ${line.medicineName}`}
                                                    />
                                                    <span className={`text-xs ${valid ? "text-[var(--ops-carbon-500)]" : "font-semibold text-[var(--ops-crimson-600)]"}`}>
                                                        {valid ? readout : "Enter a positive whole number"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 self-end">
                                                <button
                                                    type="button"
                                                    onClick={() => setInfoItemId(infoOpen ? null : line.itemId)}
                                                    disabled={!line.itemInventoryId}
                                                    aria-expanded={infoOpen}
                                                    aria-label={`Batch-wise stock for ${line.medicineName}`}
                                                    title={line.itemInventoryId ? "Batch-wise stock in eVital" : "Remove and re-add this medicine to see its batches"}
                                                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                                                        infoOpen
                                                            ? "border-[var(--ops-emerald-200)] bg-[var(--ops-emerald-50)] text-[var(--ops-emerald-600)]"
                                                            : "border-[var(--ops-carbon-200)] text-[var(--ops-carbon-700)] hover:bg-[var(--ops-carbon-50)]"
                                                    }`}
                                                >
                                                    <span aria-hidden="true" className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] leading-none">i</span>
                                                    {infoOpen ? "Hide batches" : "Batch stock"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => cart.remove(line.itemId)}
                                                    aria-label={`Remove ${line.medicineName}`}
                                                    className="h-9 px-2 text-xs font-semibold text-[var(--ops-carbon-500)] hover:text-[var(--ops-crimson-600)]"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                        {infoOpen && (
                                            <div className="border-t border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-50)] px-4 py-3">
                                                <BatchStockPanel line={line} onUseSuggested={(tablets) => cart.setQuantity(line.itemId, String(tablets))} />
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                </div>
            )}
        </Modal>
    );
}

CartModal.propTypes = { onClose: PropTypes.func.isRequired };

// Sticky bar shown whenever the cart has something in it — on every tab and page.
export default function ProcurementCart() {
    const cart = useProcurementCart();
    const [open, setOpen] = useState(false);
    if (cart.lines.length === 0 && !open) return null;
    return (
        <>
            <div className="fixed bottom-5 right-5 z-[1000] flex items-center gap-3 rounded-2xl border border-[var(--ops-emerald-200)] bg-[var(--ops-carbon-25)] px-4 py-3 shadow-lg">
                <span className="text-sm font-semibold text-[var(--ops-carbon-900)]">
                    {cart.lines.length} medicine{cart.lines.length === 1 ? "" : "s"} in cart
                </span>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="bg-elm-500 text-white-50 px-3.5 py-2 rounded-lg font-sans text-sm font-semibold"
                >
                    Review &amp; place order
                </button>
            </div>
            {open && <CartModal onClose={() => setOpen(false)} />}
        </>
    );
}
